import { type ReactNode, useEffect, useState } from 'react';

import devConfig from '../config.development.json';
import { type Config } from './config.types';
import { fillInObject } from './util/fillInObject';

const origin = typeof window !== 'undefined' ? window.location.origin : '';
const USER_PORTAL_CONFIG_URL = `${origin}/userportal.config.json`;
const CONFIG_URL = `${origin}/config.json`;
const CONFIG_OVERRIDE_URL = `${origin}/config.overrides.json`;
const CONFIG = 'config';
const CONFIG_REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface ProviderProps {
    children: ReactNode;
    useUserPortalConfigJson?: boolean;
    // If you want the config to be periodically refreshed after the app is loaded, your app has to handle cases
    // where the config is more up to date than the built javascript.
    poll?: boolean;
    // If necessary, you can specify a subset of config props at runtime
    customConfig?: Partial<Config>;
}

const fetchJson = async (url: string) => {
    const response = await fetch(url, {
        headers: {
            Accept: 'application/json',
            Pragma: 'no-cache',
            'Cache-Control': 'no-cache',
        },
    });
    let result = null;

    if (response.ok) {
        // GCP request for file not in bucket will load the index. Check content-type to ensure it's actually JSON
        const contentType = response.headers.get('content-type');
        try {
            if (contentType && contentType.includes('application/json')) {
                result = await response.json();
            }
        } catch (e) {
            console.error(`Failed to parse JSON from ${url}:`, e);
        }
    }

    return result;
};

/**
 * Tries to fetch and apply overrides to configs
 * @param config userportal.config.json
 * @returns configs with overridden values if overrides are found, else the original configs
 */
const applyOverrides = async (config: any) => {
    const override = await fetchJson(CONFIG_OVERRIDE_URL);
    if (override) {
        return fillInObject(config, override);
    }
    return config;
};

/**
 * Takes the old/ deprecated userPortal.config.json and translates it to match the appConfig.
 * This is needed as a compatibility layer for public.
 * @param userportalConfig userportal.config.json
 * @returns config.json
 */
const userPortalTranslator = (userportalConfig: any): Config => {
    // CENPLAT-10941 delete this function
    const result: Config = {
        ...userportalConfig,
    };
    // Only the segment write key and environment need to be translated other values
    // compatibility hack is only needed for central_segmentWriteKey and that it should never be need for blocksync/ cloud
    result.blocksync_segmentWriteKey = userportalConfig.segment?.writeKey ?? '';
    result.central_segmentWriteKey = userportalConfig.segment?.writeKey ?? '';
    result.environment = userportalConfig.sentryEnvironment;
    return result;
};

/**
 * Renders the children after loading the config into the session.
 * For Local development, it will use the local config.development.json file
 * If the config cannot be found show warning in console and allow consumers to manage error handling with isConfigSet()
 */
export const ProvideConfig = ({
    children,
    useUserPortalConfigJson,
    poll = false,
    customConfig = {},
}: ProviderProps) => {
    const [loaded, setLoaded] = useState(false);

    const getConfigData = async (): Promise<Config | null> => {
        if (process.env.NODE_ENV !== 'production') {
            return devConfig as Config;
        }
        const configUrl = useUserPortalConfigJson ? USER_PORTAL_CONFIG_URL : CONFIG_URL;
        const config = await fetchJson(configUrl);
        if (!config) {
            return null;
        }
        // only translate if we're in the old infrastructure. ie public
        if (useUserPortalConfigJson) {
            return userPortalTranslator(config);
        }
        // only use overrides if in deployed development slot
        if (config?.environment === 'development') {
            return applyOverrides(config);
        }
        return config;
    };

    const loadConfig = async () => {
        const config = {
            ...(await getConfigData()),
            ...customConfig,
        };

        if (config) {
            sessionStorage.setItem(CONFIG, JSON.stringify(config));
        } else {
            // eslint-disable-next-line no-console
            console.warn('Site configuration not present');
        }
    };

    const reloadConfigPeriodically = async () => {
        setTimeout(() => {
            loadConfig();
            reloadConfigPeriodically();
        }, CONFIG_REFETCH_INTERVAL);
    };

    useEffect(() => {
        loadConfig().then(() => setLoaded(true));
        if (poll) {
            reloadConfigPeriodically();
        }
    }, []);

    return loaded ? children : null;
};

/**
 * @returns The config from the session.
 */
export const getConfig = (): Config => JSON.parse(sessionStorage.getItem(CONFIG) || '{}');

/**
 * Can be used for error handling purposes for when the config fails to load
 * @returns True if there is config in the session storage
 */
export const isConfigSet = (): boolean => {
    const config = sessionStorage.getItem(CONFIG);
    if (config && config !== '{}') {
        return true;
    }
    return false;
};
